/**
 * Store cache (F5): everything a store keeper's (or a loading operator's) phone needs to work a
 * whole day offline at one place — the site's places with label payloads and paths, the bay
 * declarations in force, the lots and bags here or on their way here, open trips, count requests,
 * the keeper's replacement tag rolls, sack types and scales.
 *
 * PROPOSED endpoint (F5.3 engineering.api): GET /sites/{site_id}/store-cache. The village phone
 * downloads the same shape for its COLLECTION_POINT (F5.1: "the phone knows the bags staged at its
 * point") when the roster config names one (`collection_point_id`).
 *
 * Weights are integer GRAMS. No money figures, no farmer names or phone numbers: a lot
 * carries its code and the farmer's roster code only. Unknown extra fields are kept (loose objects).
 *
 * Storage: the whole snapshot is ONE row in the `config` table (id `store_cache`), sealed with the
 * roster vault key when there is one, plus the existing `locations`/`team` rows for the shared
 * roster readers. A site cache is a few thousand bags at most, so one row read into memory is fine
 * and avoids a schema migration.
 */
import { z } from 'zod'
import { getDb, type SupplyChainDB } from './db'
import { getVaultKey, saveSiteCache, teamMemberSchema } from './roster'
import { maybeSeal, maybeUnseal } from './vault'

const grams = z.number().int()

export const LOCATION_LEVELS = ['SITE', 'ZONE', 'BAY', 'POSITION'] as const
export type LocationLevel = (typeof LOCATION_LEVELS)[number]

export const SITE_KINDS = ['COLLECTION_POINT', 'WAREHOUSE', 'PROCESSING', 'PACKING', 'LAB'] as const

/**
 * What a place is used for inside a site. Not a model column — the spec names "a conventional
 * bay, a hold area and a sample store" (F5.3-g); PROPOSED as `role` in the cache.
 */
export const LOCATION_ROLES = [
  'STORAGE',
  'CONVENTIONAL',
  'HOLD_AREA',
  'SAMPLE_STORE',
  'DOCK',
] as const
export type LocationRole = (typeof LOCATION_ROLES)[number]

export const storeLocationSchema = z.looseObject({
  id: z.string(),
  code: z.string(),
  name: z.string().nullish(),
  level: z.enum(LOCATION_LEVELS),
  kind: z.string().nullish(),
  parent_id: z.string().nullish(),
  /** Printed LOCATION label payload (qr_codes.payload). */
  qr_payload: z.string().nullish(),
  active: z.boolean().default(true),
  /** "Godown A › Bay 3 › S11-P04" — made by the server so every phone shows the same words. */
  path: z.string().nullish(),
  role: z.enum(LOCATION_ROLES).nullish(),
})
export type StoreLocation = z.infer<typeof storeLocationSchema>

/** The STORAGE_NO_FUMIGANT declaration in force on a bay (latest per bay, F5.4). */
export const bayDeclarationSchema = z.looseObject({
  id: z.string(),
  location_id: z.string(),
  form_code: z.string().default('STORAGE_NO_FUMIGANT'),
  form_version: z.number().int(),
  clean: z.boolean(),
  signed_at: z.string(),
  valid_to: z.string(),
  signed_by_id: z.string().nullish(),
  answers: z.record(z.string(), z.unknown()).default({}),
})
export type BayDeclaration = z.infer<typeof bayDeclarationSchema>

export const formQuestionSchema = z.looseObject({
  key: z.string(),
  hi: z.string(),
  en: z.string(),
  kind: z.enum(['yes_no', 'number']),
  /** For yes_no: the answer that keeps the bay clean. */
  clean_answer: z.boolean().nullish(),
  /** Required only when this other yes_no question is answered Yes. */
  required_if: z.string().nullish(),
  unit: z.string().nullish(),
})
export type FormQuestion = z.infer<typeof formQuestionSchema>

export const declarationFormSchema = z.looseObject({
  form_code: z.string(),
  version: z.number().int(),
  questions: z.array(formQuestionSchema),
})
export type DeclarationForm = z.infer<typeof declarationFormSchema>

export const STREAMS = ['MAIN', 'CONV'] as const
export type Stream = (typeof STREAMS)[number]
export const HOLDS = ['NONE', 'QUARANTINE', 'HELD', 'RECALLED'] as const
export type Hold = (typeof HOLDS)[number]

export const storeLotSchema = z.looseObject({
  id: z.string(),
  code: z.string(),
  crop_id: z.string(),
  crop_name: z.string().nullish(),
  /** FARM | ORPHAN | BATCH … (units.unit_type) */
  unit_type: z.string().default('FARM'),
  stream: z.enum(STREAMS),
  hold: z.enum(HOLDS).default('NONE'),
  hold_reason: z.string().nullish(),
  status: z.string().default('COMMITTED'),
  committed_at: z.string().nullish(),
  /** Bags in the whole lot (not only here). */
  bag_count: z.number().int(),
  /** APP or PAPER — every bag of a PAPER lot is check-weighed at receive. */
  capture_mode: z.enum(['APP', 'PAPER']).default('APP'),
  farmer_code: z.string().nullish(),
})
export type StoreLot = z.infer<typeof storeLotSchema>

export const BAG_STATUSES = [
  // A run's output sack is TAGGED, then WEIGHED, until it is sealed or tipped into the next step.
  'TAGGED',
  'WEIGHED',
  'SEALED',
  'IN_TRANSIT',
  'STORED',
  'MISSING',
  'ISSUED',
  'CONSUMED',
  'VOID',
] as const
export type BagStatus = (typeof BAG_STATUSES)[number]

/** A bag (farm bag or output sack) known to this place: here, staged here, or coming here. */
export const storeBagSchema = z.looseObject({
  container_id: z.string(),
  qr_payload: z.string(),
  /** BAG (farm) or OUTPUT_BAG (output sack, R14). */
  tag_kind: z.enum(['BAG', 'OUTPUT_BAG']).default('BAG'),
  unit_id: z.string(),
  status: z.enum(BAG_STATUSES),
  hold: z.enum(HOLDS).default('NONE'),
  stream: z.enum(STREAMS),
  /** Open custody interval's place; null while on a trip or when it has none. */
  location_id: z.string().nullish(),
  /** Open trip it is on (IN_TRANSIT or LOADING). */
  shipment_id: z.string().nullish(),
  net_g_gate: grams.nullish(),
  net_g_current: grams.nullish(),
  tare_g: grams.default(0),
  /** taken_by_id of the PURCHASE weight — that person may not check-weigh it. */
  gate_weigher_id: z.string().nullish(),
  /** When the open interval started (days in store). */
  in_since: z.string().nullish(),
  /** Bag of an ordinary lot still without its red tie (F5.6 red-tie list). */
  red_tie_pending: z.boolean().default(false),
})
export type StoreBag = z.infer<typeof storeBagSchema>

export const storeSampleSchema = z.looseObject({
  sample_id: z.string(),
  seal_payload: z.string(),
  unit_id: z.string().nullish(),
  status: z.string(),
  location_id: z.string().nullish(),
  shipment_id: z.string().nullish(),
})
export type StoreSample = z.infer<typeof storeSampleSchema>

export const manifestItemSchema = z.looseObject({
  item_id: z.string(),
  container_id: z.string().nullish(),
  sample_id: z.string().nullish(),
  qr_payload: z.string(),
  unit_id: z.string().nullish(),
  recheck_required: z.boolean().default(false),
})
export type ManifestItem = z.infer<typeof manifestItemSchema>

/** An open trip to or from this place, with its manifest (GET /shipments?to_location_id=…). */
export const tripSchema = z.looseObject({
  shipment_id: z.string(),
  code: z.string(),
  kind: z.string().default('TRANSFER'),
  status: z.enum(['LOADING', 'IN_TRANSIT', 'RECEIVED', 'RECEIVED_SHORT']),
  from_location_id: z.string(),
  from_name: z.string().nullish(),
  to_location_id: z.string().nullish(),
  to_name: z.string().nullish(),
  vehicle_no: z.string().nullish(),
  challan_no: z.string().nullish(),
  dispatched_at: z.string().nullish(),
  items: z.array(manifestItemSchema).default([]),
})
export type Trip = z.infer<typeof tripSchema>

export const destinationSchema = z.looseObject({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  kind: z.string(),
  /** The village's usual godown — pre-selected at loading. */
  default: z.boolean().default(false),
})
export type Destination = z.infer<typeof destinationSchema>

export const countRequestSchema = z.looseObject({
  id: z.string(),
  location_id: z.string(),
  requested_at: z.string(),
  note: z.string().nullish(),
})
export type CountRequest = z.infer<typeof countRequestSchema>

/** A tag roll issued to a keeper for replacement sacks (F5-B1.a), or the village bag roll. */
export const storeRollSchema = z.looseObject({
  batch_id: z.string(),
  kind: z.string(),
  payload_prefix: z.string(),
  start_sequence: z.number().int(),
  end_sequence: z.number().int(),
  issued_to_id: z.string().nullish(),
  /** Payloads from this roll already bound or void (never reusable). */
  used: z.array(z.string()).default([]),
})
export type StoreRoll = z.infer<typeof storeRollSchema>

export const storeSackTypeSchema = z.looseObject({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  tare_g: grams,
})
export type StoreSackType = z.infer<typeof storeSackTypeSchema>

export const scaleSchema = z.looseObject({
  id: z.string(),
  serial_no: z.string().nullish(),
  name: z.string(),
})

/** Server settings the phone needs (all PROPOSED; open questions in F5.2 / F5.4 / F5-B1.a). */
export const storeSettingsSchema = z.looseObject({
  /** Days a bay declaration is valid (F5.4 open question). */
  declaration_renewal_days: z.number().int().default(90),
  /** Warn this many days before a declaration expires. */
  declaration_warn_days: z.number().int().default(7),
  /** Check-weigh picks per lot when the server did not pick (F5.2 open question; map: 3). */
  checks_per_lot: z.number().int().default(3),
  /** Receive tolerance: the larger of a fixed gram figure and a share of the gate net (bp). */
  receive_tolerance_g: grams.default(250),
  receive_tolerance_bp: z.number().int().default(50),
  /** Expected drying per day in store, basis points of gate net, by crop; `*` for others. */
  drying_bp_per_day: z.record(z.string(), z.number().int()).default({ '*': 15 }),
  /** Drying is never assumed above this (bp of gate net). */
  drying_max_bp: z.number().int().default(500),
  /** Lost-tag candidate match on net weight (F5-B1.a). */
  replace_match_tolerance_g: grams.default(500),
})
export type StoreSettings = z.infer<typeof storeSettingsSchema>

export const storeCacheSchema = z.looseObject({
  site_id: z.string(),
  site_code: z.string().nullish(),
  site_name: z.string().nullish(),
  /** COLLECTION_POINT | WAREHOUSE | PROCESSING | PACKING */
  site_kind: z.string().nullish(),
  generated_at: z.string(),
  locations: z.array(storeLocationSchema).default([]),
  declarations: z.array(bayDeclarationSchema).default([]),
  forms: z.array(declarationFormSchema).default([]),
  lots: z.array(storeLotSchema).default([]),
  bags: z.array(storeBagSchema).default([]),
  samples: z.array(storeSampleSchema).default([]),
  /** Payloads known to be VOID / RETIRED — refused with a reason and reported (TAG_VOID_SEEN). */
  dead_tags: z
    .array(z.looseObject({ qr_payload: z.string(), status: z.enum(['VOID', 'RETIRED']) }))
    .default([]),
  trips: z.array(tripSchema).default([]),
  destinations: z.array(destinationSchema).default([]),
  count_requests: z.array(countRequestSchema).default([]),
  rolls: z.array(storeRollSchema).default([]),
  sack_types: z.array(storeSackTypeSchema).default([]),
  scales: z.array(scaleSchema).default([]),
  settings: storeSettingsSchema.default(storeSettingsSchema.parse({})),
  team: z.array(teamMemberSchema).default([]),
  config: z.record(z.string(), z.unknown()).default({}),
})
export type StoreCache = z.infer<typeof storeCacheSchema>

export const STORE_CACHE_ROW_ID = 'store_cache'

/** What is kept on the phone: the snapshot plus when this phone received it. */
export interface StoredStoreCache {
  cache: StoreCache
  downloaded_at: number
}

/** Store cache age: fresh < warnHours ≤ stale < maxAgeDays ≤ expired. */
export type StoreCacheAge = 'missing' | 'fresh' | 'stale' | 'expired'

export function storeCacheAge(
  downloadedAt: number | null | undefined,
  maxAgeDays: number,
  now: number,
  warnHours = 24,
): StoreCacheAge {
  if (!downloadedAt) return 'missing'
  const age = now - downloadedAt
  if (age >= maxAgeDays * 86_400_000) return 'expired'
  if (age >= warnHours * 3_600_000) return 'stale'
  return 'fresh'
}

/** Save a downloaded store cache: the snapshot row plus locations/team for the shared readers. */
export async function saveStoreCache(
  cache: StoreCache,
  db: SupplyChainDB = getDb(),
  now: number = Date.now(),
  /** false on a village phone: its team and places come from the roster, keep them. */
  replaceShared = true,
): Promise<void> {
  const key = await getVaultKey(db)
  const row = {
    id: STORE_CACHE_ROW_ID,
    data: await maybeSeal<StoredStoreCache>(key, { cache, downloaded_at: now }),
  }
  if (replaceShared)
    await saveSiteCache(
      {
        site_id: cache.site_id,
        generated_at: cache.generated_at,
        locations: cache.locations.map((l) => ({ ...l, parent_id: l.parent_id ?? null })),
        team: cache.team,
        config: cache.config,
      },
      db,
    )
  await db.config.put(row)
}

export async function readStoreCache(
  db: SupplyChainDB = getDb(),
): Promise<StoredStoreCache | undefined> {
  const row = await db.config.get(STORE_CACHE_ROW_ID)
  if (!row) return undefined
  return maybeUnseal<StoredStoreCache>(await getVaultKey(db), row.data as StoredStoreCache)
}

/** Drop the store cache (device revoked). */
export async function wipeStoreCache(db: SupplyChainDB = getDb()): Promise<void> {
  await db.config.delete(STORE_CACHE_ROW_ID)
}
